import React, { useState, useCallback } from 'react'
import { cn } from '../../lib/utils'
import type { PresentationData, SlideTransition } from '../../types'
import { usePresentation } from '../../hooks/usePresentation'
import { Slide } from './Slide'
import { SlideProgress, SlideCounter } from './SlideProgress'
import { SlideNavigation } from './SlideNavigation'
import { PresenterMode } from '../presenter/PresenterMode'
import { SlideThumbnailsPanel } from '../viewer/SlideThumbnailsPanel'
import { KeyboardShortcutsModal } from '../viewer/KeyboardShortcutsModal'
import { ExportModal } from '../viewer/ExportModal'

export interface SlideContainerProps {
  presentation: PresentationData
  showProgress?: boolean
  showCounter?: boolean
  showNavigation?: boolean
  enableKeyboard?: boolean
  defaultTransition?: SlideTransition
  onSlideChange?: (slideIndex: number) => void
  onBackToLibrary?: () => void
  enablePresenterMode?: boolean
  className?: string
}

export const SlideContainer: React.FC<SlideContainerProps> = ({
  presentation,
  showProgress = true,
  showCounter = true,
  showNavigation = true,
  enableKeyboard = true,
  defaultTransition = 'slide',
  onSlideChange,
  onBackToLibrary,
  enablePresenterMode = true,
  className,
}) => {
  const [isPresenterMode, setIsPresenterMode] = useState(false)
  const [showThumbnails, setShowThumbnails] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showExport, setShowExport] = useState(false)

  const {
    currentSlide,
    totalSlides,
    transitionType,
    direction,
    nextSlide,
    previousSlide,
    goToSlide,
    toggleFullscreen,
  } = usePresentation({
    totalSlides: presentation.slides.length,
    onSlideChange,
    enableKeyboard,
  })

  const hasPrev = currentSlide > 0
  const hasNext = currentSlide < totalSlides - 1

  /**
   * Enhanced keyboard handler for additional shortcuts
   */
  const handleEnhancedKeyboard = useCallback((e: KeyboardEvent) => {
    // Only handle if not in input fields
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return
    }

    switch (e.key) {
      case 'p':
      case 'P':
        if (enablePresenterMode) {
          setIsPresenterMode(prev => !prev)
        }
        break
      case 't':
      case 'T':
        setShowThumbnails(prev => !prev)
        break
      case 'e':
      case 'E':
        setShowExport(true)
        break
      case '?':
        setShowShortcuts(true)
        break
    }
    return undefined
  }, [enablePresenterMode])

  React.useEffect(() => {
    if (enableKeyboard) {
      window.addEventListener('keydown', handleEnhancedKeyboard)
      return () => window.removeEventListener('keydown', handleEnhancedKeyboard)
    }
    return undefined
  }, [enableKeyboard, handleEnhancedKeyboard])

  // Presenter Mode
  if (isPresenterMode) {
    return (
      <PresenterMode
        presentation={presentation}
        currentSlideIndex={currentSlide}
        onNextSlide={nextSlide}
        onPreviousSlide={previousSlide}
        onGoToSlide={goToSlide}
        onExit={() => setIsPresenterMode(false)}
        defaultTransition={defaultTransition}
      />
    )
  }

  return (
    <div className={cn('relative w-full h-screen overflow-hidden bg-muted', className)} data-presentation-container>
      {/* Progress Bar */}
      {showProgress && (
        <SlideProgress current={currentSlide} total={totalSlides} />
      )}

      {/* Slides */}
      <div className="relative w-full h-full overflow-hidden">
        {presentation.slides.map((slideData, index) => (
          <Slide
            key={slideData.id || index}
            data={slideData}
            isActive={index === currentSlide}
            transition={slideData.transition || transitionType || defaultTransition}
            direction={direction}
          />
        ))}
      </div>

      {/* Navigation Controls */}
      {showNavigation && (
        <SlideNavigation
          onPrev={previousSlide}
          onNext={nextSlide}
          onFullscreen={toggleFullscreen}
          onBackToLibrary={onBackToLibrary}
          onPresenterMode={enablePresenterMode ? () => setIsPresenterMode(true) : undefined}
          onShowThumbnails={() => setShowThumbnails(true)}
          onShowShortcuts={() => setShowShortcuts(true)}
          onShowExport={() => setShowExport(true)}
          hasPrev={hasPrev}
          hasNext={hasNext}
          showFullscreen={true}
          showPresenterMode={enablePresenterMode}
        />
      )}

      {/* Slide Counter */}
      {showCounter && (
        <SlideCounter current={currentSlide} total={totalSlides} />
      )}

      {/* Thumbnails Panel */}
      <SlideThumbnailsPanel
        slides={presentation.slides}
        currentIndex={currentSlide}
        onSlideSelect={goToSlide}
        isOpen={showThumbnails}
        onClose={() => setShowThumbnails(false)}
      />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        presentation={presentation}
      />
    </div>
  )
}
